import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from 'react-query';

import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiPaper from '@material-ui/core/Paper';
import MuiTextField from '@material-ui/core/TextField';
import MuiBox from '@material-ui/core/Box';
import MuiTablePagination from '@material-ui/core/TablePagination';

import ReportFiles from '../../api/reportFiles';
import ReportFilesTable from './ReportFilesTable';
import ReportFilesTableRow from './ReportFilesTableRow';
import ActiveFilters from '../../components/ActiveFilters';
import ColumnFilter from '../../components/ColumnFilter';
import BulkActions from '../../components/BulkActions';

import useTableSelection from '../../hooks/useTableSelection';
import useColumnFilters from '../../hooks/use-column-filters';
const { getAll, patchFileStatusToPending, getDspOptions } = ReportFiles;

const LABEL_TITLE = 'Search Filename';

// Base column filters configuration (DSP options will be added dynamically)
const getColumnFiltersConfig = (dspOptions = []) => [
  {
    id: 'aws_modified_date',
    label: 'AWS Import Date',
    filterType: 'date',
    apiParam: 'aws_modified_date',
    valueFormatter: value => (value ? new Date(value).toLocaleDateString('en-US') : 'N/A'),
  },
  {
    id: 'dsp_name',
    label: 'DSP',
    filterType: 'select',
    apiParam: 'dsp_name__eq',
    options: dspOptions,
  },
  {
    id: 'file_url',
    label: 'File URL',
    filterType: 'text',
  },
  {
    id: 'status',
    label: 'Status',
    filterType: 'select',
    apiParam: 'status_filter',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'done', label: 'Done' },
      { value: 'error', label: 'Error' },
    ],
  },
];

function ReportFilesTableContainer(props) {
  const { filters } = props;
  const showIsIngested = filters.showIsIngested;

  const [params, setParams] = useState({ page: 0, count: 250 });
  const [updatingFileId, setUpdatingFileId] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Fetch DSP options dynamically
  const { data: dspOptions = [] } = useQuery('dsp-options', getDspOptions, {
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Create column filters config with dynamic DSP options
  const columnFiltersConfig = useMemo(() => getColumnFiltersConfig(dspOptions), [dspOptions]);

  // State for column filters
  const {
    activeFilters,
    removeFilter,
    clearAllFilters,
    clearPersistedFilters,
    getFilterParams,
    openFilterColumn,
    tempFilters,
    openFilterPopup,
    updateTempFilter,
    applyFilter,
    clearCurrentFilter,
  } = useColumnFilters(columnFiltersConfig, 'report-files');

  // Combine filter parameters with pagination parameters
  const combinedParams = useMemo(() => {
    const filterParams = getFilterParams();
    const combinedParams = { ...params };

    if (!combinedParams?.search) delete combinedParams.search;

    // Set default statuses if no status filter is applied
    if (!filterParams.status_filter) {
      combinedParams.statuses = showIsIngested ? ['done'] : ['pending', 'in_progress', 'error'];
    } else {
      // If status filter is applied, use it instead of default statuses
      combinedParams.statuses = [filterParams.status_filter];
      delete filterParams.status_filter; // Remove to avoid duplication
    }

    // Add filter parameters
    return { ...combinedParams, ...filterParams };
  }, [params, getFilterParams, showIsIngested]);

  const { data, isLoading, refetch } = useQuery(['report-files', combinedParams], () => {
    setUpdatingFileId(null);
    return getAll(combinedParams);
  });

  const { mutate: setFileStatusToPending } = useMutation(
    id => {
      setUpdatingFileId(id);
      return patchFileStatusToPending(id);
    },
    {
      onSuccess: () => {
        setUpdatingFileId(null);
        refetch();
      },
    }
  );

  // Initialize table selection hook
  const {
    selectedIds,
    selectedItems,
    isAllSelected,
    isSomeSelected,
    isSelected,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
  } = useTableSelection(data?.results || [], 'id');

  // Debug log for selection state
  React.useEffect(() => {}, [selectedIds, selectedItems.length, data?.results?.length, selectedItems]);

  function updateSearch(value) {
    setParams({ ...params, page: 0, search: value });
  }

  function handleChangePage(_event, newPage) {
    setParams({ ...params, page: newPage });
  }

  function handleChangeRowsPerPage({ target: { value: newRowsPerPage } }) {
    setParams({ ...params, page: 0, count: parseInt(newRowsPerPage, 10) });
  }

  // Functions to export selected items
  const handleExportSelected = () => {
    alert(`${selectedItems.length} items selected for export`);
  };

  // Function to set selected items to pending
  const handleBulkSetToPending = async () => {
    // Filter only items that can be set to pending (error or done status)
    const validItems = selectedItems.filter(item => ['error', 'done'].includes(item.status));

    if (validItems.length === 0) {
      alert('No items with valid status selected. Only items with "error" or "done" status can be set to pending.');
      return;
    }

    setBulkLoading(true);

    try {
      // Set each valid item to pending in parallel
      const results = await Promise.allSettled(validItems.map(item => patchFileStatusToPending(item.id)));

      // Count successful and failed operations
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      // Clear selection after operation
      clearSelection();

      // Refresh data
      refetch();

      if (failed === 0) {
        alert(`Successfully set ${successful} file${successful !== 1 ? 's' : ''} to pending`);
      } else {
        alert(`${successful} files set to pending successfully, ${failed} failed`);
      }
    } catch (error) {
      // Error setting files to pending
      alert('Error occurred while setting files to pending');
    } finally {
      setBulkLoading(false);
    }
  };

  const { search = '', page, count } = params;
  //TECH DEBT : Not really, but this is the last table that is using this type of pagination. It just makes the overall product less uniformed.
  // Render column filters in header
  const renderColumnFilters = () => {
    return columnFiltersConfig.map(column => {
      const hasFilter = activeFilters ? activeFilters.some(filter => filter.columnId === column.id) : false;
      const filter = activeFilters ? activeFilters.find(filter => filter.columnId === column.id) : null;

      // Extract values from filter for ColumnFilter component
      const filterValues =
        openFilterColumn?.id === column.id
          ? tempFilters
          : filter
            ? {
                operator: filter.operator,
                value: filter.value,
                startDate: filter.startDate,
                endDate: filter.endDate,
                min: filter.min,
                max: filter.max,
              }
            : {};

      return (
        <ColumnFilter
          key={column.id}
          columnId={column.id}
          columnLabel={column.label}
          filterType={column.filterType}
          options={column.options}
          hasFilter={hasFilter}
          filterValues={filterValues}
          onOpenFilter={openFilterPopup}
          onApplyFilter={applyFilter}
          onClearFilter={clearCurrentFilter}
          onFilterValueChange={updateTempFilter}
        />
      );
    });
  };

  return (
    <MuiBox width="100%">
      <MuiTableContainer
        component={MuiPaper}
        style={{
          overflow: 'hidden',
          overflowX: 'auto',
          maxWidth: '100%',
        }}>
        <BulkActions
          selectedCount={selectedIds.length}
          onClearSelection={clearSelection}
          onExport={handleExportSelected}
          onSetToPending={handleBulkSetToPending}
          loading={bulkLoading}
        />

        <MuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MuiBox>
            <MuiTextField
              label={LABEL_TITLE}
              value={search || ''}
              onChange={({ target: { value } }) => updateSearch(value)}
              size="small"
              variant="outlined"
            />
          </MuiBox>
          <MuiTablePagination
            rowsPerPageOptions={[250, 500, 1000]}
            component="div"
            count={data?.total || 0}
            rowsPerPage={count}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </MuiBox>

        {/* Display active filters */}
        <ActiveFilters
          filters={
            activeFilters
              ? activeFilters.reduce((acc, filter) => {
                  acc[filter.columnId] = filter;
                  return acc;
                }, {})
              : {}
          }
          columns={columnFiltersConfig}
          onClearFilter={removeFilter}
          onClearAllFilters={clearAllFilters}
          onClearPersistedFilters={process.env.NODE_ENV === 'development' ? clearPersistedFilters : undefined}
        />

        {isLoading && <MuiCircularProgress />}

        <ReportFilesTable
          onSelectAll={toggleSelectAll}
          isAllSelected={isAllSelected}
          isSomeSelected={isSomeSelected}
          columnFilters={renderColumnFilters()}>
          {data?.results.map(file => (
            <ReportFilesTableRow
              key={file.id}
              {...file}
              id={String(file.id)}
              setFileStatusToPending={() => setFileStatusToPending(file.id)}
              updatingFile={updatingFileId === file.id}
              isSelected={isSelected(file.id)}
              onToggleSelect={() => toggleSelection(file.id)}
              onBulkAction={handleBulkSetToPending}
              selectedCount={selectedIds.length}
            />
          ))}
        </ReportFilesTable>
        <MuiBox display="flex" justifyContent="flex-end">
          <MuiTablePagination
            rowsPerPageOptions={[250, 500, 1000]}
            component="div"
            count={data?.total || 0}
            rowsPerPage={count}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </MuiBox>
      </MuiTableContainer>
    </MuiBox>
  );
}

export default ReportFilesTableContainer;
